import { JACKPOT_ID } from "@shared/constants";
import { isArray } from "lodash-es";
import { Client as WebSocket } from "rpc-websockets";

const ERROR_NOT_CONNECTED = "Error: WebSocket not connected";

export class JPSocket {
  constructor() {
    this._isOpen = false;
    /**
     *
     * @type {WebSocket | null}
     * @private
     */
    this._ws = null;
  }

  /**
   * @param {string} streamId
   * @return {Promise}
   * @description Instantiate a WebSocket client and subscribe to its events
   * @see https://github.com/elpheria/rpc-websockets/blob/trunk/API.md
   */
  connect(streamId) {
    const url = this._getApiUrl(streamId);

    this._ws = new WebSocket(url);
    return this._subscribe();
  }

  /**
   * @return {Promise<{slots: Object[]|null, wins: Object[]|null}>}
   */
  async getSlotsAndWins() {
    const response = await this._call("Jackpot.GetState");
    return this._normalize(response);
  }

  /**
   @return {Promise<string>}
   */
  async getCurrency() {
    const response = await this._call("BankGroup.Get");
    return response?.Currency;
  }

  /**
   * @param {function(Object[]|null): void} listener
   * @return {void}
   */
  addSlotsListener(listener) {
    this._on("Jackpot.UpdateSlots", (response) => {
      listener(this._normalizeSlots(response?.updates));
    });
  }

  /**
   * @param {function(Object|null): void} listener
   * @return {void}
   */
  addWinsListener(listener) {
    this._on("Jackpot.WinStatus", (response) => {
      listener(this._normalizeWin(response?.win));
    });
  }

  /**
   * @param {function(Object[]|null): void} listener
   * @return {void}
   */
  addCooldownsListener(listener) {
    this._on("Jackpot.CommunityCooldown", (response) => {
      listener(response?.cooldowns);
    });
  }

  /**
   * @param {number} winId
   * @return {Promise<void>}
   */
  async acceptWin(winId) {
    await this._call("Jackpot.AcceptWin", { win_id: winId });
  }

  /**
   * @private
   * @return {Promise}
   * @description Subscribe to "open", "close" & "error" events
   */
  _subscribe() {
    return new Promise((resolve, reject) => {
      this._ws.on("open", () => {
        this._isOpen = true;
        resolve();
      });

      this._ws.on("close", () => {
        this._isOpen = false;
      });

      this._ws.on("error", (error) => {
        this._isOpen = false;
        reject(error);
      });
    });
  }

  /**
   * @private
   * @param {string} event
   * @param {Object=} params
   * @return {Promise}
   */
  _call(event, params) {
    if (!this._isOpen) {
      throw new Error(ERROR_NOT_CONNECTED);
    }

    return this._ws.call(event, params);
  }

  /**
   * @private
   * @param {string} event
   * @param {function} listener
   * @return {void}
   */
  _on(event, listener) {
    if (!this._isOpen) {
      throw new Error(ERROR_NOT_CONNECTED);
    }

    this._ws.on(event, listener);
  }

  /**
   * @private
   * @param {Promise<{slots: Object[]|null, wins: Object[]|null}>} data
   * @return {Promise<{slots: Object[]|null, wins: Object[]|null}>}
   */
  _normalize(data) {
    if (isArray(data?.slots)) {
      data.slots = this._normalizeSlots(data.slots);
    }

    if (isArray(data?.wins)) {
      data.wins = data.wins.map(this._normalizeWin);
    }

    return data;
  }

  /**
   * @private
   * @param {Object[]} slots
   * @return {Object[]}
   */
  _normalizeSlots(slots) {
    return slots
      .filter((item) => item.jackpot_id === JACKPOT_ID)
      .map(this._normalizeSlot);
  }

  /**
   * @private
   * @param {Object}
   * @return {Object}
   */
  _normalizeSlot = ({
    is_community: isCommunity,
    jackpot_id: jackpotId,
    value,
    slot,
  }) => ({
    isCommunity,
    jackpotId,
    value,
    slot,
  });

  /**
   * @private
   * @param {Object}
   * @return {Object}
   */
  _normalizeWin = ({
    jackpot_id: jackpotId,
    win_id: id,
    value,
    state,
    slot,
  }) => ({
    jackpotId,
    value,
    state,
    slot,
    id,
  });

  /**
   * @private
   * @param {string} streamId
   * @return {string}
   */
  _getApiUrl(streamId) {
    const pathname = `/ws/?stream=${streamId}`;

    if (window.location.hostname === "localhost") {
      return process.env.REACT_APP_WSS_URL + pathname;
    }

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";

    return protocol + "//" + window.location.host + pathname;
  }

  get getConnection() {
    return this._isOpen;
  }
}

export const jackpotService = new JPSocket();
